/** @jsx jsx */
import { jsx } from '@emotion/react';

import type { DescriptionPage_ProductVariant as DescriptionPageProductVariant } from '__generated__/graphql-types';

import { InlineNotification } from '@coursera/cds-core';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import { withEnrollButtonData } from 'bundles/enroll-common/components/xdp/BannerEnroll';
import type { FormattedCourse } from 'bundles/enroll-common/types/enrollSectionTypes';
import withEnrollModal from 'bundles/enroll-modal/components/withEnrollModal';
import { useUndoCancellationDataForXdp } from 'bundles/subscriptions-common/utils/cancellationUndoUtils';
import EnrollButtonContent from 'bundles/unified-description-page-common/components/enroll/consumer/EnrollButtonContent';
import type { PropsFromEnrollHocs } from 'bundles/unified-description-page-common/components/enroll/consumer/EnrollButtonContent';
import { useCourseraPlusOwnership } from 'bundles/unified-description-page-common/contexts/consumer/CourseraPlusOwnershipContext';

import _t from 'i18n!nls/xdp';

type BaseProps = {
  s12nId?: string;
  courseCount?: number;
  partnerIds?: string[];
  name: string;
  slug: string;
  courseId?: string;
  course?: FormattedCourse;
  onStickyHeader?: boolean;
  onlyShowButton?: boolean;
  isS12nUpgradeEligible?: boolean;
  s12nUpgradeLink?: string;
};

type PropsFromCoursePage = BaseProps & {
  isSpecialization: false;
  s12nProductVariant?: undefined;
  s12nCourses?: undefined;
};

type PropsFromSpecializationPage = BaseProps & {
  isSpecialization: true;
  s12nProductVariant: DescriptionPageProductVariant;
  s12nCourses: FormattedCourse[];
};

type PropsFromCaller = PropsFromCoursePage | PropsFromSpecializationPage;

// This component is used by the consumer course, specialization and professional certificate pages.
// We need to use existing enroll components so this component is wrapped by a few enroll HOCs that add data for the
// enroll modal. We take these props and give them to a common display component EnrollButtonContent that
// is also used for project pages.
const WrappedConsumerEnrollButton = ({
  onClick,
  buttonLabel,
  buttonLink,
  title,
  isEnrolled,
  isEligibleForPlusMonthlyToAnnualUpgrade,
  isCourseraPlusEligibleProduct,
  showUndoCancellationCTA,
  subtitleElem,
  buttonDisabled,
  buttonLoading,
  enrollmentAvailableChoices,
  userAgent,
  TitleComponent,
  onMultipleLearningProgramsLinkClick,
  onStickyHeader,
  isS12nUpgradeEligible,
  s12nUpgradeLink,
  explainerTextPosition,
}: PropsFromEnrollHocs) => {
  return (
    <EnrollButtonContent
      onClick={onClick}
      buttonLabel={buttonLabel}
      buttonLink={buttonLink}
      isEnrolled={isEnrolled}
      isEligibleForPlusMonthlyToAnnualUpgrade={isEligibleForPlusMonthlyToAnnualUpgrade}
      isCourseraPlusEligibleProduct={isCourseraPlusEligibleProduct}
      showUndoCancellationCTA={showUndoCancellationCTA}
      title={title}
      subtitleElem={subtitleElem}
      buttonDisabled={buttonDisabled}
      buttonLoading={buttonLoading}
      enrollmentAvailableChoices={enrollmentAvailableChoices}
      userAgent={userAgent}
      TitleComponent={TitleComponent}
      onMultipleLearningProgramsLinkClick={onMultipleLearningProgramsLinkClick}
      onStickyHeader={onStickyHeader}
      isS12nUpgradeEligible={isS12nUpgradeEligible}
      s12nUpgradeLink={s12nUpgradeLink}
      explainerTextPosition={explainerTextPosition}
    />
  );
};

// @ts-expect-error TSMIGRATION
const ButtonWithEnrollModal = withEnrollButtonData(true)(withEnrollModal(WrappedConsumerEnrollButton));

// Re-wrapping the enroll button with modal to clarify it's interface
// since the enroll modal components are not typed properly.
const ConsumerEnrollButtonNotConfigurable = ({
  s12nId,
  courseCount,
  partnerIds,
  name,
  slug,
  isSpecialization,
  courseId,
  course,
  s12nCourses,
  s12nProductVariant,
  onStickyHeader,
  onlyShowButton,
}: PropsFromCaller) => {
  const { isEligibleForPlusMonthlyToAnnualUpgrade, isCourseraPlusEligibleProduct } = useCourseraPlusOwnership() ?? {};
  const { showUndoCancellationCTA } = useUndoCancellationDataForXdp({
    productItemId: s12nId,
    isCourseraPlusEligibleProduct,
    partnerIds,
  });

  return (
    <ErrorBoundaryWithTagging
      componentName="ConsumerEnrollButtonNotConfigurable"
      errorComponent={
        onlyShowButton || onStickyHeader ? undefined : (
          <InlineNotification
            severity="error"
            title={_t('An error occurred within our enrollment service')}
            role="alert"
          >
            {_t('Our development team has been informed and is actively working towards a resolution.')}
          </InlineNotification>
        )
      }
    >
      <ButtonWithEnrollModal
        s12nId={s12nId}
        courseCount={courseCount}
        partnerIds={partnerIds}
        name={name}
        slug={slug}
        isSpecialization={isSpecialization}
        courseId={courseId}
        course={course}
        s12nCourses={s12nCourses}
        s12nProductVariant={s12nProductVariant}
        onStickyHeader={onStickyHeader}
        isEligibleForPlusMonthlyToAnnualUpgrade={Boolean(isEligibleForPlusMonthlyToAnnualUpgrade)}
        isCourseraPlusEligibleProduct={isCourseraPlusEligibleProduct}
        showUndoCancellationCTA={showUndoCancellationCTA}
        onlyShowButton={onlyShowButton}
      />
    </ErrorBoundaryWithTagging>
  );
};

export default ConsumerEnrollButtonNotConfigurable;
